// What the page is allowed to *render*. The server decides what actually happens.
//
// Every rule here is duplicated from the server on purpose. The alternative -- the server
// stamping a `can_push` onto every row -- would mean copying the shared cached book payload per
// caller, which is the one thing that payload exists not to do. So the page mirrors the rules,
// the server holds the line, and if the two ever disagree the write is refused with the owner's
// name in the message.

import { foundNotCleared } from '../lib/foundByScout.ts';
import { playgroundNeedsName, sendableContacts } from '../lib/playgroundContacts.ts';
import { isQ4Hero } from '../lib/q4Target.ts';
import { bankEmailsOf, isBankEmail } from '../book/bank.ts';
import type { BookAccount, BookPayload, Contact } from '../api/types';

function integrationReady(book: BookPayload | null): boolean {
  return Boolean(book?.integrations?.amplemarket);
}

// Whether this caller may push to *this* account. A strategist's scope is their own book, so
// the answer is per account rather than per session, and the controls appear on their own
// accounts and not on a colleague's.
export function canPushAccount(book: BookPayload | null, row: BookAccount | null): boolean {
  if (!integrationReady(book)) return false;
  const viewer = book?.viewer;
  if (!viewer?.can_push) return false;
  if (viewer.push_scope === 'all') return true;
  if (!row?.sdr_owner_email) return false;
  // Lowercased on both sides, as the server does. Clerk hands back a normalised address but
  // Salesforce keeps whatever was typed into it, and a case difference here would hide the push
  // controls on a rep's own accounts while the server went on allowing the push -- a
  // disagreement that reads as a missing feature rather than as a bug.
  return row.sdr_owner_email.toLowerCase() === String(viewer.email || '').toLowerCase();
}

export function sameEmail(left: string | null | undefined, right: string | null | undefined): boolean {
  if (!left || !right) return false;
  return left.toLowerCase() === right.toLowerCase();
}

export function canClaimAccount(book: BookPayload | null, row: BookAccount | null): boolean {
  // `POST /api/accounts/claim` refuses without this surface flag, so the control must read it
  // too. Both flip together under the rollout, so this is not a hole today -- it is the drift
  // that `test_each_sees_field_has_a_frontend_guard` exists to prevent, and dropping Manage
  // account from the sidebar left the flag with no reader in `src/` at all.
  if (!book?.viewer?.sees_account_actions) return false;
  if (!book?.viewer?.can_push || !row) return false;
  if (!row.domain) return false;
  return isBankEmail(row.sdr_owner_email, bankEmailsOf(book));
}

export function canReturnAccount(book: BookPayload | null, row: BookAccount | null): boolean {
  // Launch policy: strategist returns and snoozes resume only after a separate review.
  if (book?.viewer.role === 'strategist') return false;
  if (!book?.viewer?.can_push || !row) return false;
  return sameEmail(row.sdr_owner_email, book.viewer.email);
}

export function canSnoozeAccount(book: BookPayload | null, row: BookAccount | null): boolean {
  return canReturnAccount(book, row);
}

export function canApproveQueue(book: BookPayload | null): boolean {
  return Boolean(book?.viewer?.can_approve);
}

export function canEnrichAccount(book: BookPayload | null, row: BookAccount | null): boolean {
  if (!integrationReady(book)) return false;
  const viewer = book?.viewer;
  if (!viewer?.can_enrich) return false;
  if (viewer.enrich_scope === 'all') return true;
  if (!row?.sdr_owner_email) return false;
  return row.sdr_owner_email.toLowerCase() === String(viewer.email || '').toLowerCase();
}

// Why this person must not be pushed. Said in the page so no checkbox is offered, rather than
// one offered and then refused; the server checks the same three things independently, and it
// is the server's answer that decides.
//
// Null suppression flags are unknown, not false. A Scout save that the mirror has not
// described yet carries nulls. We do not invent a withhold from that — the whole reason
// those rows exist is so a strategist can push — and we also do not write false onto them.
export function pushBlockedReason(contact: Contact): string | null {
  if (contact.opted_out) return 'they are opted out in Salesforce';
  if (contact.bounced) return 'their email has bounced';
  if (foundNotCleared(contact) && isQ4Hero(contact.found_by_scout)) return 'they come from the Q4 research and are not in Salesforce yet, so they are not cleared to send';
  if (foundNotCleared(contact)) return 'Scout found them but Salesforce has not saved them, so they are not cleared to send';
  if (!contact.email && !contact.linkedin) return 'there is no email address or LinkedIn URL';
  // A playground person is saved to Salesforce before the send, which needs a name.
  if (playgroundNeedsName(contact)) return 'there is no name on record yet, so Salesforce cannot save them. Use Find name first';
  return null;
}

export function pushableContacts(
  book: BookPayload | null,
  row: BookAccount | null,
): Contact[] {
  if (!row || !canPushAccount(book, row)) return [];
  return sendableContacts(row).filter(contact => !pushBlockedReason(contact));
}

// Whether this row should offer a lookup at all, and it is two gaps rather than one. A
// contact with no address cannot be reached; a contact with an address and no name cannot be
// *personalised*, and 45 of the 80 sequences sampled reference `first_name` -- so the push that
// looks fine on the row is refused by most of the sequences it could go into. A contact with
// both is already reachable and already personalisable, and is not worth a call.
//
// The floor is still that there has to be something to search by. A contact with none of the
// three cannot be looked up at all.
export function enrichOffered(
  book: BookPayload | null,
  row: BookAccount | null,
  contact: Contact,
): boolean {
  if (!canEnrichAccount(book, row)) return false;
  if (contact.email && contact.name) return false;
  return Boolean(contact.name || contact.linkedin || contact.email);
}

// Which signal the server will key the lookup on. A mirror of `_find_person_for_contact` in
// app.py, and the reason the page can say what it is about to search by before the click
// rather than only naming what it hopes to find.
export type EnrichLookupKey = 'email' | 'linkedin' | 'name';

export function enrichLookupKey(contact: Contact): EnrichLookupKey {
  // An address we already hold identifies one person exactly, and the only thing left to ask
  // of it is the name -- so it wins for exactly the contact that has one and no name.
  if (contact.email && !contact.name) return 'email';
  if (contact.linkedin) return 'linkedin';
  return 'name';
}

export function contactLabel(contact: Contact): string {
  return contact.name || contact.email || contact.linkedin || 'this contact';
}

// The dynamic fields the dashboard can supply, and where each comes from. A mirror of
// `SUPPLIABLE_DYNAMIC_FIELDS` and `_lead_data()` in app.py.
export const SUPPLIABLE_FIELDS = [
  'first_name', 'last_name', 'title', 'company_name', 'company_domain', 'industry',
];

// Amplemarket does not fill a sequence's template variables from its own data -- the server
// assembles them from the book payload. Whether a variable is *required* is knowable only by
// asking: `dynamic_fields` names what the copy references, including variables behind a
// fallback that are not required at all. So this is advisory and never blocks a checkbox; the
// server pushes, and drops exactly the contacts Amplemarket names if it refuses.
export function unsatisfiedFields(
  contact: Contact,
  row: BookAccount | null,
  fields: string[] | undefined,
): string[] {
  if (!fields) return [];
  const value = (fieldName: string): string | null => {
    // Salesforce holds one name; the server splits it the same way for the lead payload.
    const [first = '', ...rest] = (contact.name ?? '').trim().split(/\s+/);
    if (fieldName === 'first_name') return first;
    if (fieldName === 'last_name') return rest.join(' ');
    if (fieldName === 'title') return contact.title;
    if (fieldName === 'company_name') return row?.account ?? null;
    if (fieldName === 'company_domain') return row?.domain ?? null;
    if (fieldName === 'industry') return row?.industry ?? null;
    return null;
  };
  return fields.filter(
    name => SUPPLIABLE_FIELDS.includes(name) && !String(value(name) || '').trim(),
  );
}
